import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, MATERIALS_QUERY_KEY } from "../api/client";
import type { Material } from "../api/types";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface MaterialPickerProps {
  value: string;
  onChange: (materialId: string) => void;
  autoFocusSearchSignal: number;
  ariaLabel: string;
}

function fallbackMaterial(materialId: string): Material {
  return {
    material_id: materialId,
    display_name: materialId,
    asset_class: "custom",
    enabled: true,
    latest_analysis_generated_at: null,
    latest_analysis_age_seconds: null,
    is_stale: false,
    next_periodic_run_at: null,
  };
}

export function MaterialPicker({ value, onChange, autoFocusSearchSignal, ariaLabel }: MaterialPickerProps) {
  const { t } = useTranslation("dashboard");
  const [open, setOpen] = useState(false);
  const previousFocusSignalRef = useRef(autoFocusSearchSignal);
  const materialsQuery = useQuery({ queryKey: MATERIALS_QUERY_KEY, queryFn: api.listMaterials });
  const materials = useMemo(() => materialsQuery.data?.materials ?? [], [materialsQuery.data]);
  const selected = materials.find((material) => material.material_id === value) ?? fallbackMaterial(value);

  useEffect(() => {
    if (previousFocusSignalRef.current === autoFocusSearchSignal) return;
    previousFocusSignalRef.current = autoFocusSearchSignal;
    setOpen(true);
  }, [autoFocusSearchSignal]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className="justify-between min-w-[14rem]"
        >
          <span className="font-mono text-sm">{selected.material_id}</span>
          <span className="ms-2 truncate text-muted-foreground">{selected.display_name}</span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder={t("materialPicker.placeholder")} />
          <CommandList>
            <CommandEmpty>
              {materialsQuery.isError
                ? t("materialPicker.error")
                : t("materialPicker.empty")}
            </CommandEmpty>
            <CommandGroup>
              {materials.map((material) => (
                <CommandItem
                  key={material.material_id}
                  value={`${material.material_id} ${material.display_name} ${material.asset_class}`}
                  onSelect={() => {
                    onChange(material.material_id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "me-2 h-4 w-4",
                      material.material_id === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="font-mono">{material.material_id}</span>
                  <span className="ms-2 text-muted-foreground">{material.display_name}</span>
                  <span className="ms-auto text-xs text-muted-foreground">{material.asset_class}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
